import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Recursion.module.css";
import FibonacciEngine from "../recursionengine/FibonacciEngine";
import FactorialEngine from "../recursionengine/FactorialEngine";
import PalindromeEngine from "../recursionengine/PalindromeEngine";
import { RecursionCommands } from "../../../types/RecursionCommands";

const PALINDROME_WORDS: { [key: number]: string[] } = {
  1: ['a', 'i'],
  2: ['aa', 'bb'],
  3: ['mom', 'dad', 'pop', 'wow', 'eye', 'bob'],
  4: ['noon', 'deed', 'peep', 'boob'],
  5: ['level', 'civic', 'kayak', 'radar', 'refer'],
  6: ['hannah', 'redder'],
  7: ['racecar', 'rotator', 'deified']
};

const RecursionCompare: React.FC = () => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationsRunning, setAnimationsRunning] = useState(0);
  const [inputValue, setInputValue] = useState(5); 
  const [fibCommand, setFibCommand] = useState<RecursionCommands | null>(null);
  const [factCommand, setFactCommand] = useState<RecursionCommands | null>(null);
  const [palCommand, setPalCommand] = useState<RecursionCommands | null>(null);
  const [fibActiveLine, setFibActiveLine] = useState<number | null>(null);
  const [fibIsAnimating, setFibIsAnimating] = useState(false);
  const [fibFinalResult, setFibFinalResult] = useState<number | null>(null);
  const [factActiveLine, setFactActiveLine] = useState<number | null>(null);
  const [factIsAnimating, setFactIsAnimating] = useState(false);
  const [factFinalResult, setFactFinalResult] = useState<number | null>(null);
  const [palInputValue, setPalInputValue] = useState('level');
  const [palActiveLine, setPalActiveLine] = useState<number | null>(null);
  const [palIsAnimating, setPalIsAnimating] = useState(false);
  const [palFinalResult, setPalFinalResult] = useState<boolean | null>(null);
  const TOTAL_COMPONENTS = 3;

  const animationResolver = useRef<(() => void) | null>(null);
  const getRandomPalindrome = (length: number): string => {
    const words = PALINDROME_WORDS[length] || PALINDROME_WORDS[5];
    return words[Math.floor(Math.random() * words.length)];
  };

  useEffect(() => {
    const newPalindrome = getRandomPalindrome(inputValue);
    setPalInputValue(newPalindrome);
  }, [inputValue]);

  useEffect(() => {
    if (animationsRunning <= 0) {
      setIsAnimating(false);
      if (animationResolver.current) {
        animationResolver.current();
        animationResolver.current = null;
      }
    }
  }, [animationsRunning]);

  const startAnimation = (num = TOTAL_COMPONENTS) => {
    if (isAnimating) return false;
    setIsAnimating(true);
    setAnimationsRunning(num);
    return true;
  };

  const handleAnimationComplete = () => {
    setAnimationsRunning((prev) => prev - 1);
  };

  const handleRunAll = () => {
    if (!startAnimation()) return;
    const command: RecursionCommands = { id: Date.now(), action: "run" };
    setFibCommand(command);
    setFactCommand(command);
    setPalCommand(command);
  };

  const handleStopAll = () => {
    const command: RecursionCommands = { id: Date.now(), action: "stop" };
    setFibCommand(command);
    setFactCommand(command);
    setPalCommand(command);
    setIsAnimating(false);
  };

  const handleResetAll = () => {
    const command: RecursionCommands = { id: Date.now(), action: "reset" };
    setFibCommand(command);
    setFactCommand(command);
    setPalCommand(command);
    setIsAnimating(false);
    setFibFinalResult(null);
    setFactFinalResult(null);
    setPalFinalResult(null);
  };

  const handleGenerateAll = () => {
    const randomValue = Math.floor(Math.random() * 4) + 3;
    setInputValue(randomValue);
    const command: RecursionCommands = { id: Date.now(), action: "generate" };
    setFibCommand(command);
    setFactCommand(command);
    setPalCommand(command);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 7) {
      setInputValue(value);
    }
  };

return (
  <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>
    <h1 className={styles.title}>Recursion Arena</h1>

    <div className={styles.description}>
      <h2>Description</h2>
      <p>
        Imagine solving a problem by handing smaller pieces of it down a chain of helpers. 
        Each helper follows the same steps — until one reaches a simple answer and sends it back up.
         Whether it’s multiplying down in <b>factorial</b>, branching out in <b>fibonacci</b>, or checking inward in a <b>palindrome</b>, recursion 
         is all about solving smaller versions of the same puzzle until the full answer unfolds.
      </p>
    </div>

    <div className={styles.useCase}>
      <h2>Use Cases</h2>
      <ul>
        <li>
          <b>Fibonacci:</b> Great for demonstrating exponential blow-up.
        </li>
        <li>
          <b>Factorial:</b> Perfect for illustrating linear recursion depth.
        </li>
        <li>
          <b>Palindrome:</b> Showcases divide-and-conquer on strings.
        </li>
      </ul>
    </div>

    <div className={styles.inputSection}>
      <input
        id="recursionInput"
        type="number"
        value={inputValue}
        onChange={handleInputChange}
        disabled={isAnimating}
        min="1"
        max="7"
        className={styles.sharedInput}
      />
      <span className={styles.inputNote}>
        💡 Tip: Values of 5 or less provide the clearest visualizations
      </span>
    </div>

    <div className={styles.controls}>
      <button onClick={handleRunAll} disabled={isAnimating}>
        Run
      </button>
      <button onClick={handleStopAll}>Stop</button>
      <button onClick={handleResetAll}>Reset</button>
      <button onClick={handleGenerateAll} disabled={isAnimating}>
        Generate
      </button>
    </div>

    <div className={styles.recursionCompareViz}>
      <div className={styles.recursionCompareFib}>
        <h2>Fibonacci Sequence</h2>
        <FibonacciEngine
          command={fibCommand}
          onComplete={handleAnimationComplete}
          inputValue={String(inputValue)}
          setActiveLine={setFibActiveLine}
          isAnimating={fibIsAnimating}
          setIsAnimating={setFibIsAnimating}
          setFinalResult={setFibFinalResult}
          finalResult={fibFinalResult}
        />
      </div>

      <div className={styles.recursionCompareFact}>
        <h2>Factorial</h2>
        <FactorialEngine
          command={factCommand}
          onComplete={handleAnimationComplete}
          inputValue={String(inputValue)}
          setActiveLine={setFactActiveLine}
          isAnimating={factIsAnimating}
          setIsAnimating={setFactIsAnimating}
          setFinalResult={setFactFinalResult}
          finalResult={factFinalResult}
        />
      </div>

      <div className={styles.recursionComparePal}>
        <h2>Palindrome Checker</h2>
        <PalindromeEngine
          command={palCommand}
          onComplete={handleAnimationComplete}
          inputValue={palInputValue}
          setActiveLine={setPalActiveLine}
          isAnimating={palIsAnimating}
          setIsAnimating={setPalIsAnimating}
          setFinalResult={setPalFinalResult}
          finalResult={palFinalResult}
        />
      </div>
    </div>
  </div>
  );
};

export default RecursionCompare;